import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { Redirect } from 'react-router-dom'
import { Radio } from 'antd'
import Heading from 'components/shared/Heading'
import OrderNavigation from 'components/shared/OrderNavigation'
import AddressSearch from './AddressSearch'
import CadastreSearch from './CadastreSearch'
import ManualSearch from './ManualSearch'
import ExistingEstate from './ExistingEstate'
import './index.scss'

function initialAddressSearch(webOrder) {
  if (webOrder) {
    const { street, postalCode, postalArea } = webOrder.address
    return `${street ? street + ',' : ''} ${postalCode || ''} ${
      postalArea || ''
    }`.trim()
  }

  return null
}

function initialCadastreSearch(webOrder) {
  if (webOrder) {
    const { knr, gnr, bnr } = webOrder.address
    if (knr && gnr && bnr) {
      return { knr, gnr, bnr }
    }
  }

  return {}
}

function initialManualSearch(webOrder) {
  if (webOrder) {
    const { address } = webOrder
    return {
      street: address.street,
      postalCode: address.postalCode,
      postalArea: address.postalArea,
      location: address.location || null,
    }
  }

  return {}
}

function allCadastreSetInWebOrder(webOrder) {
  const s = initialCadastreSearch(webOrder)
  return s.knr && s.gnr && s.bnr
}

function getInitialTab(webOrder) {
  if (!webOrder) {
    return 'address'
  }

  let initialTab = ''
  if (allCadastreSetInWebOrder(webOrder)) {
    initialTab = 'cadastre'
  } else if (webOrder.cadastreAddressId) {
    initialTab = 'address'
  } else {
    initialTab = 'manual'
  }

  return initialTab
}

const IdentifyEstate = ({ webOrder, estate, estateReducer, goToNextStep }) => {
  const [selectedAddress, setSelectedAddress] = useState(estate.address)
  const [promptExistingEstate, setPromptExistingEstate] = useState(false)
  const [newOrderOnEstateId, setNewOrderOnEstateId] = useState(false)
  const [currentTab, setCurrentTab] = useState(getInitialTab(webOrder))

  const handleAddressSelection = (selection) => {
    if (!selection) {
      return
    }

    const { address } = selection

    if (
      address &&
      address.id &&
      selectedAddress &&
      address.id === selectedAddress.id
    ) {
      setSelectedAddress(null)
    } else {
      setSelectedAddress(address)
      if (address && address.id) {
        setPromptExistingEstate(true)
      }
    }
  }

  const onProceed = () => {
    estateReducer({ type: 'setAddress', address: selectedAddress })
    goToNextStep()
  }

  const changeTab = (e) => {
    setCurrentTab(e.target.value)
  }

  if (newOrderOnEstateId) {
    return <Redirect to={`/oppdrag/${newOrderOnEstateId}/ny-bestilling`} />
  }

  const hasValidSelectedAddress = !!(
    selectedAddress &&
    selectedAddress.street &&
    selectedAddress.postalCode &&
    selectedAddress.postalArea &&
    selectedAddress.location
  )

  const tabs = (
    <Radio.Group
      buttonStyle="solid"
      size="small"
      value={currentTab}
      onChange={changeTab}
    >
      <Radio.Button value="address">Addressesøk</Radio.Button>
      <Radio.Button value="cadastre">Matrikkelsøk</Radio.Button>
      <Radio.Button value="manual">Manuelt</Radio.Button>
    </Radio.Group>
  )

  return (
    <div className="new-order identify-estate">
      <Heading level={2}>Finn adresse</Heading>

      {currentTab === 'address' && (
        <AddressSearch
          initialSearch={initialAddressSearch(webOrder)}
          selectedAddress={selectedAddress}
          onSelect={handleAddressSelection}
          tabs={tabs}
        />
      )}

      {currentTab === 'cadastre' && (
        <CadastreSearch
          initialSearch={initialCadastreSearch(webOrder)}
          onSelect={handleAddressSelection}
          tabs={tabs}
        />
      )}

      {currentTab === 'manual' && (
        <ManualSearch
          initialValues={initialManualSearch(webOrder)}
          onUpdate={handleAddressSelection}
          tabs={tabs}
        />
      )}

      {hasValidSelectedAddress && selectedAddress.id && (
        <div className="selected-address">
          <Heading level={3}>Valgt adresse</Heading>
          <div>{selectedAddress.street}</div>
          <div>
            {selectedAddress.postalCode} {selectedAddress.postalArea}
          </div>
        </div>
      )}

      {hasValidSelectedAddress && promptExistingEstate && (
        <ExistingEstate
          cadastreId={selectedAddress.id}
          onNewOrderOnExistingEstate={(estate) =>
            setNewOrderOnEstateId(estate.id)
          }
          onNewEstate={() => setPromptExistingEstate(false)}
        />
      )}

      <OrderNavigation
        onNext={onProceed}
        nextButtonProps={{
          disabled: !hasValidSelectedAddress,
        }}
      />
    </div>
  )
}

IdentifyEstate.propTypes = {
  webOrder: PropTypes.object,
}

export default IdentifyEstate
